import { useState } from 'react';
import { Users, DollarSign, PieChart, CheckCircle2, GraduationCap, LogOut, Shield, AlertCircle, Clock, ArrowLeft, FileCheck } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import KpiGrid from '../../components/agents/dashboard/KpiGrid';
import AnalyticsPanel from '../../components/agents/dashboard/AnalyticsPanel';
import EarningsPanel from '../../components/agents/dashboard/EarningsPanel';
import ClientsTable from '../../components/agents/dashboard/ClientsTable';
import QuickActions from '../../components/agents/dashboard/QuickActions';
import { logoutUser } from '../../utils/authUtils';
import { toast } from 'react-toastify';

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState('client');
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const handleLogout = () => {
    logoutUser();
    toast.success('Logged out successfully');
    window.location.href = '/signin';
  };

  // Client Registration section data
  const kpis = [
    { title: 'Earnings this month', value: '₵0.00', delta: '0%', icon: DollarSign },
    { title: 'Total earnings (YR-to)', value: '₵0.00', delta: '0%', icon: DollarSign },
    { title: 'Registrations this month', value: '0', delta: '0%', icon: Users },
    { title: 'Total Registrations (all-time)', value: '0', delta: '0%', icon: PieChart },
    { title: 'Client Conversion', value: '0%', delta: '0%', icon: CheckCircle2 },
  ];

  const donutData = {
    sources: [
      { value: 0, color: '#10b981' },
      { value: 0, color: '#f59e0b' },
    ],
    gender: [
      { value: 0, color: '#10b981' },
      { value: 0, color: '#3b82f6' },
    ],
    age: [
      { value: 0, color: '#10b981' },
      { value: 0, color: '#f59e0b' },
      { value: 0, color: '#3b82f6' },
      { value: 0, color: '#ef4444' },
    ],
    location: [
      { value: 0, color: '#10b981' },
      { value: 0, color: '#f59e0b' },
      { value: 0, color: '#3b82f6' },
      { value: 0, color: '#a855f7' },
    ],
  };

  const monthly = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ].map((m) => ({ label: m, value: 0 }));

  const clients = [];

  // Insurance Sales section data
  const salesKpis = [
    { title: 'Policies sold (month)', value: '0', delta: '0%', icon: CheckCircle2 },
    { title: 'Gross premium (month)', value: '₵0.00', delta: '0%', icon: DollarSign },
    { title: 'Avg. premium per policy', value: '₵0.00', delta: '0%', icon: PieChart },
    { title: 'Total policies YTD', value: '0', delta: '0%', icon: Users },
    { title: 'Cancellation rate', value: '0%', delta: '0%', icon: PieChart },
  ];

  const salesDonutData = {
    sources: [
      { value: 0, color: '#3b82f6' }, 
      { value: 0, color: '#10b981' }, 
    ],
    gender: [
      { value: 0, color: '#10b981' }, 
      { value: 0, color: '#3b82f6' }, 
    ],
    age: [
      { value: 0, color: '#10b981' }, 
      { value: 0, color: '#f59e0b' }, 
      { value: 0, color: '#3b82f6' }, 
      { value: 0, color: '#ef4444' }, 
    ],
    location: [
      { value: 0, color: '#10b981' }, 
      { value: 0, color: '#f59e0b' }, 
      { value: 0, color: '#3b82f6' }, 
      { value: 0, color: '#a855f7' }, 
    ],
  };

  const salesMonthly = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ].map((m) => ({ label: m, value: 0 }));

  const salesPolicies = [];

  const isActivated = user?.status !== 0;

  return (
    <div className="px-4 pb-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div>
        <div className="flex flex-col gap-4 mt-6 mb-6 sm:flex-row sm:justify-between sm:items-start sm:gap-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold break-words sm:text-xl md:text-2xl">
              Welcome back, {user?.first_name || 'Agent'}!
            </h1>
            <p className="mt-1 text-sm text-gray-600 line-clamp-2 sm:line-clamp-none">
              {activeSection === 'client'
                ? 'Track registrations, earnings and conversion at a glance'
                : 'Monitor sales volume, premiums and policy performance'}
            </p>
            <div className="mt-2 text-xs text-gray-500 break-words">
              Agent Code: {user?.agent_code} | Status: {isActivated ? 'Active' : 'Inactive'}
            </div>
          </div>
          <div className="flex flex-wrap flex-shrink-0 gap-2 sm:flex-nowrap">
            <button
              onClick={() => setActiveSection('client')}
              className={
                `px-3 py-2 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap ` +
                (activeSection === 'client'
                  ? 'text-emerald-700 bg-emerald-50 border-emerald-100 hover:bg-emerald-100'
                  : 'text-gray-700 bg-white hover:bg-gray-50')
              }
            >
              Client Registration
            </button>
            <button
              onClick={() => setActiveSection('sales')}
              className={
                `px-3 py-2 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap ` +
                (activeSection === 'sales'
                  ? 'text-emerald-700 bg-emerald-50 border-emerald-100 hover:bg-emerald-100'
                  : 'text-gray-700 bg-white hover:bg-gray-50')
              }
            >
              Insurance Sales
            </button>
            <button
              onClick={() => navigate('/event-media-sales')}
              className={
                `px-3 py-2 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap ` +
                'text-gray-700 bg-white border-gray-200 hover:bg-gray-50 hover:border-emerald-100'
              }
            >
              Event & Media Sales
            </button>
            <button
              onClick={handleLogout}
              className="flex gap-1 items-center px-3 py-2 text-xs font-medium text-red-700 whitespace-nowrap bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>

        {activeSection === 'client' ? (
          <>
            <QuickActions />
            <KpiGrid items={kpis} />
            <AnalyticsPanel donutData={donutData} />
            
            <div className="mb-6">
              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href="/others-clients"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 transition-colors hover:bg-emerald-100"
                >
                  <Users className="mr-2 w-4 h-4" />
                  View Others Clients
                </a>
                <a
                  href="/students-clients"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
                >
                  <GraduationCap className="mr-2 w-4 h-4" />
                  View Student Clients
                </a>
              </div>
            </div>
            
            <ClientsTable clients={clients} title="My Registered Clients" />
            <EarningsPanel monthly={monthly} />
          </>
        ) : !isActivated ? (
          <div className="overflow-hidden relative p-5 mx-auto mt-6 max-w-md bg-white rounded-xl border-2 border-amber-200 shadow-lg sm:p-6 sm:rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 opacity-50" />
            <div className="relative">
              <div className="flex justify-center mb-3">
                <div className="flex justify-center items-center w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full shadow-lg">
                  <Shield className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 text-center mb-1.5">
                Account Under Review
              </h2>
              <p className="mb-4 text-xs text-center text-gray-600 sm:text-sm">
                Your account is being reviewed by our team
              </p>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3.5 mb-4 space-y-2.5">
                <div className="flex items-start space-x-2">
                  <Clock className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-snug text-gray-700 sm:text-sm">
                    Review in progress. Typically takes <strong>24-48 hours</strong>.
                  </p>
                </div>
                <div className="flex items-start pt-2 space-x-2 border-t border-amber-200">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-snug text-gray-700 sm:text-sm">
                    <strong>Restricted:</strong> Cannot register clients, process purchases, or withdraw commissions.
                  </p>
                </div>
                <div className="flex items-start pt-2 space-x-2 border-t border-amber-200">
                  <FileCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-snug text-gray-700 sm:text-sm">
                    You can still access your dashboard to view account information.
                  </p>
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setActiveSection('client')}
                  className="inline-flex gap-2 items-center px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm sm:text-base"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go to Client Registration
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <KpiGrid items={salesKpis} />
            <AnalyticsPanel donutData={salesDonutData} />
            <ClientsTable clients={salesPolicies} title="My Insured Clients" />
            <EarningsPanel monthly={salesMonthly} />
          </>
        )}
      </div>
    </div>
  );
}
